import {Component, OnInit} from '@angular/core';
import {Api} from '../../shared/api.service';
import {GeoSpreadPerAge} from '../../shared/api/messagecentrals/proxy/statistics/api.models';
import {Utils} from '../../shared/utils.service';
import {ActivatedRoute} from "@angular/router";
import {MessagecentralInterface} from "../../shared/api/messagecentrals/api.models";
import 'rxjs/add/observable/forkJoin';
import {Observable} from 'rxjs/Observable';
import {Platform} from "../../shared/api/messagecentrals/proxy/platforms/api.models";
import {BehaviorSubject} from "rxjs/BehaviorSubject";

class Column {
    players: number    = 0;
    targets: number    = 0;
    percentile: number = 0;
}

class Row {
    label: string;
    columns: Column[] = [];
}

@Component({
    selector: 'statistics-profiles',
    template: require('./profilesPerGeo.component.html'),
    styles: [require('./profilesPerGeo.component.scss')]
})

export class ProfilesPerGeoComponent implements OnInit {
    static readonly FILTER_REGION1 = 'geoRegion1';
    static readonly FILTER_REGION2 = 'geoRegion2';
    static readonly FILTER_REGION3 = 'geoRegion3';
    static readonly FILTER_REGION4 = 'geoRegion4';

    static readonly VIEW_PERCENTILE = 'percentile';
    static readonly VIEW_TARGETS    = 'targets';
    static readonly VIEW_PLAYERS    = 'players';

    protected readonly filters: string[] = [
        ProfilesPerGeoComponent.FILTER_REGION1,
        ProfilesPerGeoComponent.FILTER_REGION2,
        ProfilesPerGeoComponent.FILTER_REGION3,
        ProfilesPerGeoComponent.FILTER_REGION4
    ];

    protected readonly views: string[] = [
        ProfilesPerGeoComponent.VIEW_PERCENTILE,
        ProfilesPerGeoComponent.VIEW_TARGETS,
        ProfilesPerGeoComponent.VIEW_PLAYERS
    ];

    protected currentFilter: string     = ProfilesPerGeoComponent.FILTER_REGION1;
    protected currentFilterLock: boolean = false;
    protected currentView: string       = ProfilesPerGeoComponent.VIEW_PERCENTILE;
    protected currentGeo: string        = undefined;
    protected source: GeoSpreadPerAge[] = [];
    protected rows: Row[]               = [];
    protected geos: string[]            = [];

    protected defaultThresholds: any = {
        'percentile': {
            type : 'percentile',
            value: 10
        },
        'players'   : {
            type : 'number',
            value: 500
        },
        'targets'   : {
            type : 'number',
            value: 500
        }
    };

    protected currentThreshold: any = this.defaultThresholds.percentile;

    protected platformsSubject: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
    protected platformsObservable: Observable<any[]>   = this.platformsSubject.asObservable();
    protected messagecentralHash: any = {};
    protected platformsHash: any = {};

    protected columns: any[] = [
        { min: 18, max: 25 },
        { min: 26, max: 35 },
        { min: 36, max: 45 },
        { min: 46, max: 54 },
        { min: 55, max: null },
        { min: null, max: null }
    ];

    protected fixedFilterForGeo = {
        'NL' : ProfilesPerGeoComponent.FILTER_REGION1,
        'BE' : ProfilesPerGeoComponent.FILTER_REGION2
    };

    protected selectedPlatform: string;
    protected platformName: string;
    protected isLoading: boolean = false;

    constructor(private route: ActivatedRoute, private api: Api, private utils: Utils) {}

    ngOnInit() {
        let messagecentrals: MessagecentralInterface[] = this.route.snapshot.data['messagecentrals'];
        let observables: Observable<any>[]             = [];

        messagecentrals.forEach((messagecentral: MessagecentralInterface) => {
            observables.push(this.api.messagecentrals().proxy(messagecentral.id).platforms().collection());
            this.messagecentralHash[messagecentral.id] = messagecentral;
        });

        Observable.forkJoin(observables).subscribe((result) => {
            let list = [];

            messagecentrals.forEach((messagecentral: MessagecentralInterface, index: number) => {
                result[index].forEach((platform: Platform) => {
                    let item = {
                        id:                 [messagecentral.id, platform.id].join(','),
                        platformName:       platform.defaultDomain || platform.name,
                        messagecentralName: messagecentral.name,
                    };

                    list.push(item);
                    this.platformsHash[item.id] = item;
                });
            });

            this.platformsSubject.next(list);
        });
    }

    reload(reloadFromApi: boolean = true) {
        if (reloadFromApi === false) {
            return this.populateStatisticsTable(this.source);
        }

        let messagecentralId = Number.parseInt(this.selectedPlatform.split(',')[0]);
        let platformId       = this.selectedPlatform.split(',')[1];

        this.rows         = [];
        this.isLoading    = true;
        this.platformName = this.platformsHash[this.selectedPlatform].platformName;

        let groupBy = this.currentFilter.replace(/[A-Z]/, (string: string) => {
            return '_' + string.toLowerCase();
        });

        this.api.messagecentrals().proxy(messagecentralId).statistics().geoSpreadPerAge(platformId, groupBy).subscribe((result: GeoSpreadPerAge[]) => {
            this.source    = result;
            this.isLoading = false;
            return this.populateStatisticsTable(this.source);
        });
    }

    viewChanged() {
        this.currentThreshold = this.defaultThresholds[this.currentView];
    }

    private populateStatisticsTable(result: GeoSpreadPerAge[]) {
        // let columnHash = {};
        let rowHash    = {};
        this.rows      = [];
        this.geos      = [];
        this.currentFilterLock = false;

        result.forEach((data: GeoSpreadPerAge) => {
            if (this.geos.indexOf(data.geoIso) === -1) {
                this.geos.push(data.geoIso);
            }
        });

        if (this.geos.indexOf(this.currentGeo) === -1 || this.currentGeo === undefined) {
            this.currentGeo = this.geos[0] || this.geos[1];
        }

        if (this.fixedFilterForGeo[this.currentGeo] !== undefined) {
            this.currentFilter     = this.fixedFilterForGeo[this.currentGeo];
            this.currentFilterLock = true;
        }

        result.forEach((data: GeoSpreadPerAge) => {
            if (data.geoIso !== this.currentGeo) {
                return;
            }

            let key         = data[this.currentFilter];
            let columnIndex = this.determineColumnIndex(data);
            let type        = data.isFake ? 'players' : 'targets';

            if (rowHash[key] === undefined) {
                let row = rowHash[key] = new Row();
                row.label = key;

                for (let i = 0; i < this.columns.length; i++) {
                    row.columns.push(new Column());
                }

                this.rows.push(row);
            }

            rowHash[key].columns[columnIndex][type] += data.count;

            if (rowHash[key].columns[columnIndex].targets > 0) {
                rowHash[key].columns[columnIndex].percentile = Math.round(
                    rowHash[key].columns[columnIndex].players /
                    rowHash[key].columns[columnIndex].targets *
                    100
                );
            }
        });

        this.utils.sortList(this.rows, 'label');
    }

    private determineColumnIndex(stat: GeoSpreadPerAge): number {
        let index = 0;

        for (let i = 0; i <= this.columns.length; i++ ) {
            if (
                (stat.age >= this.columns[i].min || this.columns[i].min === null) &&
                (stat.age <= this.columns[i].max || this.columns[i].max === null)
            ) {
                index = i;
                break;
            }
        }

        return index;
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./src/app/insights/profilesPerGeo/profilesPerGeo.component.ts